/**
 * XSSDetector - Lógica de detección de patrones XSS
 * 
 * Analiza contenido (strings) buscando patrones XSS comunes: tags <script>,
 * protocolo javascript:, y event handlers (onerror, onclick, etc.). Retorna
 * severidad fundamentada en CVSS/OWASP/CWE y muestra del payload detectado.
 */

import SeverityCalculator from '../core/severity-calculator.js';
import { EVENT_CATALOG } from '../core/event-catalog.js';

export class XSSDetector {
	constructor(severityCalculator = null) {
		this.severityCalculator = severityCalculator || new SeverityCalculator(EVENT_CATALOG);
	}

	detect(content, context = {}) {
		if (!content || typeof content !== 'string') {
			return null;
		}

		const patterns = {
			scriptTag: /<script[^>]*>.*?<\/script>/gi,
			javascriptProtocol: /javascript:/gi,
			// SVG-based vectors — checked before generic eventHandlers so SVG payloads
			// Covers: <svg onload=...>, <svg/onload=...>, <svg\nonload=...>
			// Requires a real attribute boundary (space, /, comma, ;, quote, () before
			// "on" so <svg aria-controls="..."> (which contains "...ontrols=") doesn't
			// false-positive — see US #<ADO-ticket> false-positive audit.
			svgOnload: /<svg\b[^>]*[\s/,;"'(]on\w+\s*=/i,
			// No word boundary before "on" would match "aria-controls=", "condition=",
			// "configuration=" (the "on" substring lands mid-word). Require "on" not be
			// preceded by a word char or hyphen so it only fires at a genuine attribute
			// boundary — see US #<ADO-ticket> false-positive audit.
			eventHandlers: /(?:^|[^\w-])on\w+\s*=/gi,
			// Bare <iframe>/<object>/<embed> tags are extremely common in legitimate
			// pages (video embeds, payment widgets). Require an actual suspicious
			// signal inside the tag: inline event handler, srcdoc with content, or a
			// javascript:/data: scheme src — see US #<ADO-ticket> false-positive audit.
			iframeObjectEmbed: /<(?:iframe|object|embed)\b[^>]*(?:(?:^|[^\w-])on\w+\s*=|srcdoc\s*=\s*['"][^'"]|(?:src|data)\s*=\s*['"]?\s*(?:javascript|data)\s*:)[^>]*>/gi,
			styleExpression: /expression\s*\(/gi,
			styleUrlJs: /url\s*\(\s*['"]?\s*javascript\s*:/gi,
			metaRefresh: /<\s*meta[^>]+http-equiv\s*=\s*['"]?refresh/gi,
			// Intentionally left broad (no content-shape fix): a <base href> is only
			// dangerous when its target origin differs from the page's real origin,
			// which this string-only detector cannot determine without DOM context.
			// Reviewed in the US #<ADO-ticket> false-positive audit and left as-is —
			// see test/xss-detector.base-tag.test.js for the regression guard.
			baseTag: /<\s*base\s+[^>]+href/gi,
			// String.fromCharCode / eval / Function() constructor — all equivalent
			// eval paths for encoding and executing arbitrary JS payloads.
			// \bFunction\s*\( only matches standalone Function( not myFunction(
			fromCharCodeEval: /(?:fromCharCode|eval)\s*\(/gi,
			functionConstructor: /\bFunction\s*\(\s*['"`]/g,
			// Unicode escape obfuscation: \u003c (<), \u003e (>), \u0022 ("), \u0027 (')
			// Used to smuggle HTML tag characters through filters that check raw strings.
			unicodeObfuscation: /\\u003[ce]|\\u0022|\\u0027/i,
			// HTML entity encoding of < (&#60; &#x3c;) and > (&#62; &#x3e;)
			// Commonly used to bypass WAF rules that scan for literal angle brackets.
			htmlEntityObfuscation: /&#x?(?:3[ce]|60|62);/i,
			// Template literal with dangerous JS expression — detects ${alert(...)},
			// ${document.cookie}, ${fetch(...)} injected into JS or DOM contexts.
			templateLiteral: /\$\{[^}]*(?:alert\s*\(|document\.cookie|window\.location|eval\s*\(|fetch\s*\(|XMLHttpRequest)[^}]*\}/i,
			// mXSS: CDATA injection ("<![CDATA[") used in SVG/MathML innerHTML contexts
			// to bypass sanitizers that strip <script> but allow CDATA sections.
			mxssPattern: /<!\s*\[CDATA\[/i,
			// React dangerouslySetInnerHTML — if this appears in DOM-bound content it
			// means an attacker-controlled value is being rendered directly as HTML.
			dangerouslySetInnerHtml: /dangerouslySetInnerHTML\s*[=:{]/,
			// <iframe srcdoc="..."> injects HTML without a src URL, bypassing src-based CSP rules
			srcdocIframe: /<iframe[^>]+srcdoc\s*=/i,
			// MathML injection — <math>/<mglyph>/<malignmark> used in mXSS bypass chains
			mathmlXss: /<(?:math|mtext|annotation-xml|mglyph|malignmark)\b/i,
			// data:image/svg+xml;base64, — base64-encoded SVG/HTML not caught by existing data_protocol
			base64DataUri: /data\s*:\s*(?:image\/svg\+xml|text\/html|text\/xml|application\/xhtml\+xml)[^,]{0,30};\s*base64,/i,
			// Double-percent-encoded characters (%253c, %2527) — bypasses single-pass URL decoders
			nestedEncoding: /%25(?:3[cCeE]|27|22|2[fF]|5[cC])/i
		};

		for (const [name, pattern] of Object.entries(patterns)) {
			const matches = content.match(pattern);
			if (matches && matches.length > 0) {
				const variant = this.determineVariant(name, content, context);
				const sample = this.extractSample(content);
				
				const fullContext = {
					...context,
					content: content
				};
				
				const result = this.severityCalculator.calculate('xss', variant, fullContext);
				
				return {
					detected: true,
					severity: result.severity,
					sample: sample,
					variant: result.variant,
					cvssScore: result.cvssScore,
					cvssVector: result.cvssVector,
					owaspCategory: result.owaspCategory,
					cweId: result.cweId,
					mitreTechnique: result.mitreTechnique
				};
			}
		}

		return null;
	}

	determineVariant(patternName, content, context) {
		const contentLower = content.toLowerCase();
		
		if (patternName === 'scriptTag') {
			if (contentLower.includes('cookie') || contentLower.includes('document.cookie')) {
				return 'cookie_theft';
			}
			return 'script_tag';
		}
		
		if (patternName === 'javascriptProtocol') {
			if (contentLower.includes('cookie') || contentLower.includes('document.cookie')) {
				return 'cookie_theft';
			}
			return 'javascript_protocol';
		}
		
		if (patternName === 'eventHandlers') {
			return 'event_handler';
		}

		if (patternName === 'iframeObjectEmbed') {
			return 'iframe_object_embed';
		}

		if (patternName === 'styleExpression') {
			return 'style_expression';
		}

		if (patternName === 'styleUrlJs') {
			return 'style_url_js';
		}

		if (patternName === 'metaRefresh') {
			return 'meta_refresh';
		}

		if (patternName === 'baseTag') {
			return 'base_tag';
		}

		if (patternName === 'fromCharCodeEval') {
			return 'obfuscation';
		}

		if (patternName === 'svgOnload') {
			return 'svg_onload';
		}

		if (patternName === 'unicodeObfuscation') {
			return 'unicode_obfuscation';
		}

		if (patternName === 'htmlEntityObfuscation') {
			return 'html_entity_obfuscation';
		}

		if (patternName === 'templateLiteral') {
			return 'template_literal';
		}

		if (patternName === 'mxssPattern') {
			return 'mxss_pattern';
		}

		if (patternName === 'functionConstructor') {
			return 'function_constructor';
		}

		if (patternName === 'dangerouslySetInnerHtml') {
			return 'dangerously_set_inner_html';
		}

		if (patternName === 'srcdocIframe') {
			return 'srcdoc_iframe';
		}

		if (patternName === 'mathmlXss') {
			return 'mathml_xss';
		}

		if (patternName === 'base64DataUri') {
			return 'base64_data_uri';
		}

		if (patternName === 'nestedEncoding') {
			return 'nested_encoding';
		}

		return null;
	}

	extractSample(content, maxLength = 200) {
		if (!content) return '';
		let sample = content.trim();
		if (sample.length > maxLength) {
			sample = sample.substring(0, maxLength) + '...';
		}
		return sample;
	}
}

export default XSSDetector;

